import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { usePagination, useDialog } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'

// Mock data
import { shiftApi } from '../../api/shiftApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const Shifts = () => {
  const navigate = useNavigate()
  const dialog = useDialog()
  const [shifts, setShifts] = useState([])
  const [assignments, setAssignments] = useState([])

  const [loading, setLoading] = useState(false)
  const [assignmentsLoading, setAssignmentsLoading] = useState(false)
  const [totalShifts, setTotalShifts] = useState(0)
  const [totalAssignments, setTotalAssignments] = useState(0)
  const [selectedShift, setSelectedShift] = useState(null)
  const pagination = usePagination(5)
  const [shiftForm, setShiftForm] = useState({
    name: '',
    startTime: '09:00',
    endTime: '17:00',
    breakStart: '13:00',
    breakEnd: '14:00',
    gracePeriod: 15,
    isActive: true,
    color: '#1976d2',
  })

  const [assignmentDialog, setAssignmentDialog] = useState({
    open: false,
    selectedAssignment: null,
    shiftId: ''
  })

  const handleEditAssignment = (assignment) => {
    // Find the shift ID based on the shift name if possible, or use the ID if available
    const shift = shifts.find(s => s.shift_name === (assignment.shift_name || assignment.shift))
    setAssignmentDialog({
      open: true,
      selectedAssignment: assignment,
      shiftId: shift ? shift.id : ''
    })
  }

  const handleAssignmentSubmit = async () => {
    try {
      if (!assignmentDialog.selectedAssignment || !assignmentDialog.shiftId) {
        toast.error('Please select a shift')
        return
      }

      await shiftApi.updateAssignment({
        employee_id: assignmentDialog.selectedAssignment.employee_id || assignmentDialog.selectedAssignment.id,
        shift_id: assignmentDialog.shiftId
      })

      toast.success('Shift assignment updated successfully')
      setAssignmentDialog({ open: false, selectedAssignment: null, shiftId: '' })
      fetchAssignments()
    } catch (error) {
      console.error('Failed to update assignment:', error)
      toast.error('Failed to update assignment')
    }
  }

  const fetchShifts = async () => {
    try {
      setLoading(true)
      const response = await shiftApi.listShifts()
      setShifts(response.data.shifts || [])
      setTotalShifts(response.data.total || 0)
    } catch (error) {
      console.error('Failed to fetch shifts:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAssignments = async () => {
    try {
      setAssignmentsLoading(true)
      const response = await shiftApi.getAssignments()
      setAssignments(response.data.assignments || [])
      setTotalAssignments(response.data.total || response.data.assignments?.length || 0)
    } catch (error) {
      console.error('Failed to fetch assignments:', error)
    } finally {
      setAssignmentsLoading(false)
    }
  }

  React.useEffect(() => {
    fetchShifts()
    fetchAssignments()
  }, [pagination.page, pagination.rowsPerPage])

  const handleAddShift = () => {
    setSelectedShift(null)
    setShiftForm({
      name: '',
      startTime: '09:00',
      endTime: '17:00',
      breakStart: '13:00',
      breakEnd: '14:00',
      gracePeriod: 15,
      isActive: true,
      color: '#1976d2',
    })
    dialog.open()
  }

  const handleEditShift = async (shift) => {
    try {
      setLoading(true)
      const response = await shiftApi.getShift(shift.id)
      const fullShift = response.data

      setSelectedShift(fullShift)
      setShiftForm({
        name: fullShift.shift_name,
        startTime: fullShift.start_time?.slice(0, 5),
        endTime: fullShift.end_time?.slice(0, 5),
        breakStart: fullShift.break_start?.slice(0, 5) || '13:00',
        breakEnd: fullShift.break_end?.slice(0, 5) || '14:00',
        gracePeriod: fullShift.grace_period || 15,
        isActive: fullShift.is_active,
        color: fullShift.color || '#1976d2',
      })
      dialog.open()
    } catch (error) {
      console.error('Failed to fetch shift details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteShift = async (shift) => {
    if (window.confirm(`Delete shift "${shift.shift_name}"?`)) {
      try {
        await shiftApi.deleteShift(shift.id)
        fetchShifts()
      } catch (error) {
        console.error('Failed to delete shift:', error)
      }
    }
  }

  const handleToggleStatus = async (shift) => {
    try {
      const payload = {
        shift_name: shift.shift_name,
        start_time: shift.start_time?.slice(0, 5),
        end_time: shift.end_time?.slice(0, 5),
        break_start: shift.break_start?.slice(0, 5),
        break_end: shift.break_end?.slice(0, 5),
        grace_period: shift.grace_period,
        is_active: !(shift.status === 'active' || shift.is_active === true),
        color: shift.color
      }
      await shiftApi.updateShift(shift.id, payload)
      fetchShifts()
    } catch (error) {
      console.error('Failed to update shift status:', error)
    }
  }

  const handleFormSubmit = async () => {
    try {
      if (!shiftForm.name.trim()) {
        toast.error('Shift name is required')
        return
      }

      const payload = {
        shift_name: shiftForm.name,
        start_time: shiftForm.startTime,
        end_time: shiftForm.endTime,
        break_start: shiftForm.breakStart,
        break_end: shiftForm.breakEnd,
        grace_period: shiftForm.gracePeriod,
        is_active: shiftForm.isActive,
        color: shiftForm.color,
      }

      if (selectedShift) {
        await shiftApi.updateShift(selectedShift.id, payload)
      } else {
        await shiftApi.createShift(payload)
      }
      dialog.close()
      fetchShifts()
    } catch (error) {
      console.error('Failed to save shift:', error)
    }
  }

  const calculateDuration = (start, end) => {
    const [startHour, startMinute] = start.split(':').map(Number)
    const [endHour, endMinute] = end.split(':').map(Number)

    let totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute)
    if (totalMinutes < 0) totalMinutes += 24 * 60

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return `${hours}h ${minutes}m`
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }} >
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Add/Edit Shift Dialog */}
        <Dialog open={dialog.isOpen} onClose={dialog.close} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedShift ? 'Edit Shift' : 'Create New Shift'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                autoFocus
                fullWidth
                label="Shift Name"
                value={shiftForm.name}
                onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                margin="normal"
                required
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TimePicker
                      label="Start Time"
                      value={new Date(`2000-01-01T${shiftForm.startTime}`)}
                      onChange={(newValue) => {
                        const timeString = newValue.toTimeString().slice(0, 5)
                        setShiftForm({ ...shiftForm, startTime: timeString })
                      }}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TimePicker
                      label="End Time"
                      value={new Date(`2000-01-01T${shiftForm.endTime}`)}
                      onChange={(newValue) => {
                        const timeString = newValue.toTimeString().slice(0, 5)
                        setShiftForm({ ...shiftForm, endTime: timeString })
                      }}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Break Start"
                    type="time"
                    value={shiftForm.breakStart}
                    onChange={(e) => setShiftForm({ ...shiftForm, breakStart: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Break End"
                    type="time"
                    value={shiftForm.breakEnd}
                    onChange={(e) => setShiftForm({ ...shiftForm, breakEnd: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Grace Period (minutes)"
                type="number"
                value={shiftForm.gracePeriod}
                onChange={(e) => setShiftForm({ ...shiftForm, gracePeriod: parseInt(e.target.value) })}
                margin="normal"
                InputProps={{ inputProps: { min: 0, max: 60 } }}
              />

              <TextField
                fullWidth
                label="Color"
                type="color"
                value={shiftForm.color}
                onChange={(e) => setShiftForm({ ...shiftForm, color: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Switch
                  checked={shiftForm.isActive}
                  onChange={(e) => setShiftForm({ ...shiftForm, isActive: e.target.checked })}
                />
                <Typography>Active Shift</Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={dialog.close}>Cancel</Button>
            <Button variant="contained" onClick={handleFormSubmit}>
              {selectedShift ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Shift Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddShift}
            >
              Create Shift
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                    <ScheduleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {totalShifts}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Shifts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {shifts.reduce((sum, shift) => sum + (shift.employees || 0), 0)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Employees Assigned
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      8h
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Avg. Shift Duration
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>
                    <CalendarIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {shifts.filter(s => s.status === 'active' || s.is_active).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Shifts
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Shifts Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {shifts.map((shift) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={shift.id}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {shift.shift_name}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditShift(shift)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteShift(shift)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <StatusChip
                      status={shift.status}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Timing"
                        secondary={`${shift.start_time} - ${shift.end_time}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Duration"
                        secondary={shift.duration}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Employees"
                        secondary={shift.employees}
                      />
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      onClick={() => handleToggleStatus(shift)}
                      startIcon={(shift.status === 'active' || shift.is_active) ? <PauseIcon /> : <PlayArrowIcon />}
                    >
                      {(shift.status === 'active' || shift.is_active) ? 'Pause' : 'Activate'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/org/shifts/${shift.id}`)}
                    >
                      Manage
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Shift Assignments Table */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Current Shift Assignments
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/org/shifts/assignments')}
            >
              Manage Assignments
            </Button>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Avg. Check-in</TableCell>
                  <TableCell>Avg. Check-out</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignmentsLoading ? (
                  <TableSkeleton columns={5} rows={pagination.rowsPerPage} avatar />
                ) : assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <ScheduleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" color="textSecondary">
                        No shift assignments found
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Assign employees to shifts to see them here
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments
                    .slice(pagination.page * pagination.rowsPerPage, pagination.page * pagination.rowsPerPage + pagination.rowsPerPage)
                    .map((emp, index) => (
                      <TableRow key={`${emp.id}-${index}`} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                              {(emp.name || emp.employee_name || '?').charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {emp.employee_name || emp.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {emp.employee_code || emp.employeeId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emp.shift_name || emp.shift}
                            size="small"
                            sx={{ bgcolor: shifts.find(s => s.shift_name === (emp.shift_name || emp.shift))?.color || '#1976d2', color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={emp.department || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            {emp.avg_check_in_time || '--:--'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            {emp.avg_check_out_time || '--:--'}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )))}

              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalAssignments}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>
        {/* Quick Actions */}
        {/* <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Shift Rotation
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Manage shift rotations and assignments
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/org/shifts/assignments')}
                >
                  Manage Rotations
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Bulk Assignments
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Assign multiple employees to shifts at once
                </Typography>
                <Button variant="outlined" fullWidth>
                  Bulk Assignment
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid> */}
      </Paper>
    </Box >
  )
}

export default Shifts